<template>
    <section class="payment-settings" aria-labelledby="payment-settings-title">
        <div class="payment-settings__head">
            <div>
                <h3 id="payment-settings-title">Moyens de paiement acceptés</h3>
                <p>Choisissez les moyens et opérateurs proposés à la caisse.</p>
            </div>
            <span class="payment-settings__country">Pays de l’établissement : {{ countryName }}</span>
        </div>

        <p class="payment-settings__notice">Le paiement en espèces est disponible. Mobile Money peut être activé avec un portefeuille du centre. Chèque et virement restent indisponibles jusqu’à la mise en place du rapprochement bancaire.</p>
        <p v-if="error" class="payment-settings__error" role="alert">{{ error }}</p>
        <p v-if="success" class="payment-settings__success" role="status">{{ success }}</p>
        <div v-if="loading" class="payment-settings__loading" role="status">Chargement des moyens de paiement…</div>

        <div v-else-if="ready" class="payment-settings__grid">
            <fieldset class="payment-settings__methods" :disabled="saving">
                <legend>Moyens</legend>
                <label v-for="method in methodOptions" :key="method.key" class="payment-settings__choice">
                    <input type="checkbox" v-model="methods[method.key]" :disabled="method.unavailable">
                    <span><strong>{{ method.label }}</strong><small>{{ method.description }}</small></span>
                </label>
            </fieldset>

            <fieldset class="payment-settings__operators" :disabled="saving || !country">
                <legend>Opérateurs Mobile Money</legend>
                <p class="payment-settings__hint" v-if="!country">Renseignez d’abord le pays dans l’identité de l’établissement.</p>
                <template v-else>
                    <p class="payment-settings__hint">Les opérateurs désactivés restent dans l’historique et ne seront plus proposés pour les nouveaux paiements.</p>
                    <div v-for="operator in operators" :key="operator.id || operator.label" class="payment-settings__operator">
                        <label class="payment-settings__choice">
                            <input type="checkbox" v-model="operator.active">
                            <span><strong>{{ operator.label }}</strong></span>
                        </label>
                        <label class="payment-settings__wallet">
                            Portefeuille du centre
                            <input type="text" v-model.trim="operator.wallet_account" maxlength="80" :disabled="!operator.active" placeholder="Numéro ou identifiant marchand" autocomplete="off">
                        </label>
                    </div>
                    <p v-if="operators.length === 0" class="payment-settings__hint">Aucun opérateur proposé pour ce pays. Vous pouvez en ajouter un.</p>
                    <div class="payment-settings__add">
                        <label for="new-payment-operator">Nouvel opérateur</label>
                        <div>
                            <input id="new-payment-operator" v-model.trim="newOperator" maxlength="80" type="text" placeholder="Nom de l’opérateur" @keyup.enter="addOperator">
                            <button type="button" class="btn btn-secondary-light-o btn-radius" @click="addOperator">Ajouter</button>
                        </div>
                    </div>
                </template>
            </fieldset>
        </div>

        <div class="payment-settings__actions">
            <button v-if="!ready && !loading" type="button" class="btn btn-secondary-light-o btn-radius" @click="load">Réessayer</button>
            <button type="button" class="btn btn-primary btn-radius" :disabled="!ready || loading || saving" @click="save">
                {{ saving ? 'Enregistrement…' : 'Enregistrer les moyens de paiement' }}
            </button>
        </div>
    </section>
</template>

<script>
import Api from '@/api/api.js'

const OPTIONS = [
    { key: 'cash', label: 'Espèces', description: 'Billetage et rendu de monnaie.' },
    { key: 'mobile_money', label: 'Mobile Money', description: 'Opérateur et référence de transaction.' },
    { key: 'cheque', label: 'Chèque', description: 'Indisponible : rapprochement bancaire à mettre en place.', unavailable: true },
    { key: 'transfer', label: 'Virement', description: 'Indisponible : rapprochement bancaire à mettre en place.', unavailable: true }
]

export default {
    name: 'PaymentMethodsSettings',
    data() {
        return {
            methodOptions: OPTIONS,
            methods: { cash: true, mobile_money: false, cheque: false, transfer: false },
            operators: [],
            country: '',
            newOperator: '',
            loading: false,
            saving: false,
            ready: false,
            error: '',
            success: ''
        }
    },
    computed: {
        countryName() {
            return { TG: 'Togo', CI: 'Côte d’Ivoire', CD: 'RDC', CG: 'Congo' }[this.country] || this.country || 'Non renseigné'
        }
    },
    mounted() { this.load() },
    methods: {
        async load() {
            this.loading = true
            this.error = ''
            this.success = ''
            try {
                const response = await Api().get('/parametres/paiements/moyens')
                const data = response.data
                this.country = data.country || ''
                this.methods = Object.assign({}, data.methods, { cheque: false, transfer: false })
                this.operators = (data.operators || []).map(operator => Object.assign({}, operator))
                this.ready = true
            } catch (error) {
                this.ready = false
                this.error = this.errorMessage(error, 'Impossible de charger les moyens de paiement. Réessayez.')
            } finally {
                this.loading = false
            }
        },
        addOperator() {
            const label = this.newOperator.trim()
            if (!label || !this.country) return
            if (this.operators.length >= 40) {
                this.error = 'La liste est limitée à 40 opérateurs.'
                return
            }
            if (this.operators.some(operator => operator.label.toLocaleLowerCase() === label.toLocaleLowerCase())) {
                this.error = 'Cet opérateur figure déjà dans la liste.'
                return
            }
            this.operators.push({ id: null, label, active: false, wallet_account: '' })
            this.newOperator = ''
            this.error = ''
            this.success = ''
        },
        async save() {
            if (!this.ready || this.saving) return
            this.error = ''
            this.success = ''
            this.saving = true
            try {
                const response = await Api().put('/parametres/paiements/moyens', {
                    methods: Object.assign({}, this.methods, { cheque: false, transfer: false }),
                    operators: this.operators.map(({ id, label, active, wallet_account }) => ({ id, label, active, wallet_account }))
                })
                this.methods = Object.assign({}, response.data.methods, { cheque: false, transfer: false })
                this.operators = (response.data.operators || []).map(operator => Object.assign({}, operator))
                this.success = 'Moyens de paiement enregistrés.'
            } catch (error) {
                this.error = this.errorMessage(error, 'Les moyens de paiement n’ont pas été enregistrés. Réessayez.')
            } finally {
                this.saving = false
            }
        },
        errorMessage(error, fallback) {
            const response = error && error.response
            if (response && response.status === 401) return 'Votre session a expiré. Reconnectez-vous.'
            if (response && response.status === 403) return 'Un administrateur est requis pour ce paramètre.'
            const errors = response && response.data && response.data.errors
            if (errors) {
                const first = Object.keys(errors)[0]
                if (first && errors[first] && errors[first][0]) return errors[first][0]
            }
            return fallback
        }
    }
}
</script>

<style scoped lang="scss">
.payment-settings {
    margin: 1.5rem 0;
    padding: 1.5rem;
    border: 1px solid #d8e0ed;
    border-radius: 14px;
    background: #fff;
    color: var(--ui-text, #34415f);
}
.payment-settings__head { display: flex; align-items: flex-start; justify-content: space-between; gap: 1rem; }
.payment-settings__head h3 { margin: 0 0 .3rem; font-size: 1.35rem; font-weight: 700; }
.payment-settings__head p, .payment-settings__hint { margin: 0; color: var(--ui-text, #596783); }
.payment-settings__country { white-space: nowrap; color: var(--ui-text, #43516b); font-weight: 600; }
.payment-settings__notice { margin: 1rem 0; padding: .8rem 1rem; border-left: 2px solid #6685bf; background: #f2f5fa; color: var(--ui-text, #34415f); }
.payment-settings__grid { display: grid; grid-template-columns: minmax(220px, 1fr) minmax(280px, 1.35fr); gap: 1.8rem; }
.payment-settings fieldset { min-width: 0; margin: 0; padding: 0; border: 0; }
.payment-settings legend { width: auto; margin-bottom: .7rem; font-size: 1.15rem; font-weight: 700; color: var(--ui-text, #34415f); }
.payment-settings__choice { display: flex; align-items: flex-start; gap: .7rem; margin: 0; padding: .65rem .2rem; border-bottom: 1px solid #e6eaf1; cursor: pointer; }
.payment-settings__choice input { flex: none; margin-top: .25rem; width: 17px; height: 17px; accent-color: #3d70ec; }
.payment-settings__choice strong { display: block; font-weight: 600; color: var(--ui-text, #263550); }
.payment-settings__choice small { display: block; margin-top: .1rem; font-size: .9rem; color: var(--ui-text, #596783); }
.payment-settings__operator { padding-bottom: .7rem; border-bottom: 1px solid #e6eaf1; }
.payment-settings__operator .payment-settings__choice { border-bottom: 0; }
.payment-settings__wallet { display: block; margin: .1rem 0 0 1.9rem; color: var(--ui-text, #43516b); font-size: .9rem; font-weight: 600; }
.payment-settings__wallet input { display: block; width: 100%; max-width: 360px; margin-top: .25rem; padding: .5rem .65rem; border: 1px solid #a9b8d2; border-radius: 7px; color: var(--ui-text, #263550); font-size: 1rem; }
.payment-settings__wallet input:disabled { background: #f2f5fa; color: var(--ui-text-secondary, #697790); }
.payment-settings__add { margin-top: 1rem; }
.payment-settings__add label { display: block; margin-bottom: .35rem; font-weight: 600; }
.payment-settings__add > div { display: flex; gap: .6rem; }
.payment-settings__add input { flex: 1; min-width: 0; padding: .55rem .7rem; border: 1px solid #a9b8d2; border-radius: 7px; color: var(--ui-text, #263550); }
.payment-settings__add input:focus, .payment-settings__wallet input:focus, .payment-settings button:focus-visible, .payment-settings__choice input:focus-visible { outline: 3px solid #8bb2ff; outline-offset: 2px; }
.payment-settings__actions { display: flex; justify-content: flex-end; gap: .5rem; margin-top: 1.25rem; }
.payment-settings__error { margin: .7rem 0; color: #a3342c; font-weight: 600; }
.payment-settings__success { margin: .7rem 0; color: #226448; font-weight: 600; }
.payment-settings__loading { padding: 1rem 0; color: var(--ui-text, #43516b); }
@media (max-width: 800px) {
    .payment-settings__head { flex-direction: column; }
    .payment-settings__country { white-space: normal; }
    .payment-settings__grid { grid-template-columns: 1fr; }
}
@media (max-width: 540px) {
    .payment-settings { margin: 1rem 0; padding: 1rem; }
    .payment-settings__add > div { flex-direction: column; }
}
</style>
